import React from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_learn-assessments.scss';
import '../styles/pages/_quiz-completion.scss';

interface ModuleSection {
  title: string;
  content: string[];
  illustrationText?: string;
  additionalSection?: {
    title: string;
    content: string[];
  };
  additionalSection2?: {
    title: string;
    content: string[];
  };
}

const moduleContent: ModuleSection[] = [
  {
    title: 'Managing Thoughts and Mental Patterns',
    content: [
      '<strong>Overview:</strong> Drawing from PHQ-9 and GAD-7, this module focuses on your thought patterns, beliefs, and how they shape your mood and motivation.',
      '<br/>',
      '<strong>Key Topics:</strong>',
      '• How thoughts influence emotions',
      '• Common cognitive distortions (unhelpful thinking patterns)',
      '• Building healthy mental habits',
      '<br/>',
      '<strong>Discussion:</strong> Negative thinking is not a flaw–it\'s a habit. Like any habit, it can be changed.'
    ]
  },
    {
      title: 'How Thoughts Influence Emotions',
    content: [
      'Your thoughts are the lens through which you interpret the world, and they directly shape your emotional experience. This cognitive-behavioral connection is at the heart of mental health.',
      '<br/>',
      '<strong>The Thought-Emotion Cycle:</strong>',
      '• <strong>Situation:</strong> Something happens in your environment',
      '• <strong>Thought:</strong> Your mind automatically interprets the situation',
      '• <strong>Emotion:</strong> Your body responds with feelings based on that interpretation',
      '• <strong>Behavior:</strong> Your emotion drives your actions',
      '<br/>',
      '<strong>Example:</strong> You receive feedback that your work could be improved.',
      '• <strong>Negative Thought:</strong> "I\'m incompetent and will never succeed"',
      '• <strong>Resulting Emotion:</strong> Depression, hopelessness',
      '• <strong>Behavior:</strong> Give up trying, avoid challenges',
      '<br/>',
      '<strong>Positive Thought:</strong> "This is an opportunity to grow and improve"',
      '• <strong>Resulting Emotion:</strong> Motivation, optimism',
      '• <strong>Behavior:</strong> Seek feedback, work harder'
    ]
  },
  {
    title: 'Common Cognitive Distortions',
    content: [
      'Cognitive distortions are unhelpful thinking patterns that distort reality and create emotional distress. Here are some common ones:',
      '<br/>',
      '<strong>Catastrophizing:</strong> "Everything\'s falling apart." → "Some things are difficult, but I can manage."',
      '<br/>',
      '<strong>Self-Blame:</strong> "It\'s all my fault." → "I did my best with what I knew."',
      '<br/>',
      '<strong>Overgeneralizing:</strong> "I failed once, so I\'ll always fail." → "One setback doesn\'t define me."',
      '<br/>',
      '<strong>All-or-Nothing Thinking:</strong> "If I\'m not perfect, I\'m a failure." → "I can be good enough without being perfect."',
      '<br/>',
      '<strong>Mind Reading:</strong> "They must think I\'m stupid." → "I don\'t know what they\'re thinking; I should ask."',
      '<br/>',
      '<strong>Emotional Reasoning:</strong> "I feel worthless, so I must be worthless." → "My feelings don\'t define my worth."',
      '<br/>',
      '<strong>Should Statements:</strong> "I should always be happy." → "It\'s normal to have difficult emotions sometimes."'
    ]
  },
  {
    title: 'Building Healthy Mental Habits',
    content: [
      'Changing thought patterns requires consistent practice. Here are effective techniques for building mental resilience:',
      '<br/>',
      '<strong>Thought Journaling:</strong> Write down negative thoughts, then challenge them with evidence and create balanced alternatives.',
      '<br/>',
      '<strong>Cognitive Restructuring:</strong> Actively question and reframe distorted thinking patterns.',
      '<br/>',
      '<strong>Mindfulness Practice:</strong> Observe thoughts without judgment, recognizing they are mental events, not facts.',
      '<br/>',
      '<strong>Positive Data Log:</strong> Collect evidence that contradicts negative beliefs about yourself.',
      '<br/>',
      '<strong>Graded Exposure:</strong> Gradually face feared situations to build confidence and change avoidance patterns.',
      '<br/>',
      '<strong>Self-Compassion:</strong> Treat yourself with the same kindness you would offer a friend.',
      '<br/>',
      '<strong>Activity:</strong> Try "thought journaling" this week: write a negative thought, then challenge it with evidence and a balanced statement.',
      '<br/>',
      '<strong>Example:</strong>',
      '• <strong>Negative Thought:</strong> "I\'m a failure because I made a mistake"',
      '• <strong>Evidence Against:</strong> "I\'ve succeeded many times before, everyone makes mistakes"',
      '• <strong>Balanced Alternative:</strong> "Making mistakes is part of learning and growth"'
    ],
    additionalSection: {
      title: 'Takeaway',
      content: [
        'Awareness of your mental dialogue is the foundation of mental health and resilience. Your thoughts are not facts–they are interpretations that you can choose to change. By consistently practicing cognitive restructuring, you build the mental strength to face life\'s challenges with clarity and confidence.',
        '<br/>',
        '<strong>Remember:</strong> You are not your thoughts. You are the observer who can choose which thoughts to believe and act upon.'
      ]
    }
  },
  {
    title: 'Ready to Test Your Knowledge?',
    content: [
      'You\'ve completed Module 3: Managing Thoughts and Mental Patterns! Now, let\'s reinforce your learning with a quick quiz.'
    ],
    additionalSection: {
      title: 'Next Steps',
      content: [
        'Click "Take the Quiz" below to test your knowledge about cognitive patterns and thought management.',
        'The quiz will help you review key concepts about how thoughts influence emotions and how to build healthy mental habits.'
      ]
    }
  }
];

const EmotioPsychoRationalModule3Page: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress, isCompleted } = useModuleProgress('emotioPsychoRationalM3');
  const totalSteps = moduleContent.length;

  const safeCurrentStep = Math.max(0, Math.min(currentStep, totalSteps - 1));
  const currentModule = moduleContent[safeCurrentStep];
  const progress = ((safeCurrentStep + 1) / totalSteps) * 100;

  const handleNext = async () => {
    if (currentStep < totalSteps - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      await saveProgress(nextStep, false);
    } else {
      await saveProgress(currentStep, true);
      navigate('/panel/learn/emotio-psycho-rational-module-3/quiz');
    }
  };

  const handleBack = async () => {
    if (currentStep === 0) {
      navigate('/panel/learn/emotio-psycho-rational-resources');
      return;
    }
    const prevStep = currentStep - 1;
    setCurrentStep(prevStep);
    await saveProgress(prevStep, isCompleted);
  };

  const showCompletionMessage = currentStep === totalSteps - 1;

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading your progress...</p>
      </div>
    );
  }

  if (!currentModule) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Error loading module content. Please try refreshing the page.</p>
      </div>
    );
  }

  return (
    <div className="module-form">
      <div className="module-container">
        <div 
          className="module-left emotio-psycho-rational-panel"
        >
          <div className="module-header">
            <h1>Module 3</h1>
            <p className="module-subtitle">Managing Thoughts and Mental Patterns</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Learn how your thought patterns shape your emotions and mood. Discover cognitive distortions and build healthy mental habits based on PHQ-9 and GAD-7 principles.
            </p>
          </div>
        </div>

        <div className="module-right">
          <div className="module-content"> 
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Page {currentStep + 1} of {totalSteps}
              </span>
            </div>

            <div className="module-section">
              <h2 className="module-title"> 
                {currentModule.title}
              </h2>
              <div className="content-section">
                {currentModule.content.map((paragraph, index) => (
                  <p key={index} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                ))}
              </div>
            </div>

            {currentModule.additionalSection && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection.content.map((paragraph: string, index: number) => (
                    <p key={`additional-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
            {currentModule.additionalSection2 && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection2.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection2.content.map((paragraph: string, index: number) => (
                    <p key={`additional2-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="navigation-buttons">
            <button
              className="btn-secondary"
              onClick={handleBack}
              disabled={false} 
            >
              {currentStep === 0 ? 'Exit Module' : 'Previous'}
            </button>
            
            {showCompletionMessage ? (
              <button
                className="btn-primary"
                onClick={() => navigate('/panel/learn/emotio-psycho-rational-module-3/quiz')}
              >
                Take the Quiz
              </button>
            ) : (
              <button
                className="btn-primary"
                onClick={handleNext}
              >
                Next
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmotioPsychoRationalModule3Page;


